import type { ChainMetric, ChainMetricFormat } from './types';

export function fmtNumber(value: number | null | undefined, maxDecimals = 1): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const rounded = Math.round(value * 10 ** maxDecimals) / 10 ** maxDecimals;
  if (rounded === Math.trunc(rounded)) return Math.trunc(rounded).toLocaleString();
  return rounded.toLocaleString(undefined, { maximumFractionDigits: maxDecimals });
}

export function fmtCurrency(value: number | null | undefined, symbol = '$'): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${symbol}${Math.round(value).toLocaleString()}`;
}

export function fmtPercent(value: number | null | undefined, signed = false): string {
  if (value == null || !Number.isFinite(value)) return '—';
  const magnitude = `${Math.abs(value).toFixed(1)}%`;
  if (!signed || value === 0) return magnitude;
  return value > 0 ? `+${magnitude}` : `-${magnitude}`;
}

export function fmtHours(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return '—';
  return `${value.toFixed(1)}h`;
}

export function fmtByFormat(value: number | null | undefined, format: ChainMetricFormat, currencySymbol = '$'): string {
  switch (format) {
    case 'currency':
      return fmtCurrency(value, currencySymbol);
    case 'percent':
      return fmtPercent(value);
    case 'hours':
      return fmtHours(value);
    default:
      return fmtNumber(value);
  }
}

export function fmtMetricValue(metric: Pick<ChainMetric, 'value' | 'format'>, currencySymbol = '$'): string {
  return fmtByFormat(metric.value, metric.format, currencySymbol);
}

/** Compact "Jul 6 – Jul 19" style range label, matching L2C's period-header convention. */
export function fmtShortPeriodRange(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00Z`);
  const endDate = new Date(`${end}T00:00:00Z`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  const sameYear = startDate.getUTCFullYear() === endDate.getUTCFullYear();
  const fmt = (d: Date, withYear: boolean) =>
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: withYear ? 'numeric' : undefined, timeZone: 'UTC' });
  if (start === end) return fmt(startDate, !sameYear);
  if (sameYear && startDate.getUTCMonth() === endDate.getUTCMonth()) {
    const month = startDate.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
    return `${month} ${startDate.getUTCDate()}–${endDate.getUTCDate()}`;
  }
  return `${fmt(startDate, !sameYear)} – ${fmt(endDate, !sameYear)}`;
}
